import React, { useState } from 'react';
import styled from 'styled-components';
import Icon from './Icon';

/**
 * What you can do with the leads you have ticked.
 *
 * This used to sit between the filters and the table, so selecting a lead
 * pushed the table down, and every checkbox you ticked re-wrapped the row and
 * moved it again — the list you were reading kept sliding away from you.
 *
 * It sticks to the bottom of the viewport now. The table never moves, and the
 * bar is in the same place whether you selected the first row or the fortieth.
 *
 * The resting state is three controls. Enrichment has real per-field pricing
 * worth showing at the moment of choosing — a mobile costs roughly ten times an
 * address — but ten controls on a bar somebody sees every time they tick a box
 * is how a toolbar turns into a form.
 */

/*
 * Sticky rather than fixed.
 *
 * A fixed bar would have to be told how wide the sidebar is and follow it when
 * it collapses - a page reaching into the layout to position itself. Sticking
 * to the bottom of the content column gets the same result for free, and stays
 * correct on mobile where the sidebar is not there at all.
 */
const Dock = styled.div`
  position: sticky;
  bottom: 1rem;
  z-index: 60;
  margin-top: 1rem;
`;

const Bar = styled.div`
  pointer-events: auto;
  max-width: 1180px;
  margin: 0 auto;
  background: var(--surface-2);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  padding: 0.7rem 0.85rem;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Count = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  padding-right: 0.4rem;
  margin-right: 0.15rem;
  border-right: 1px solid var(--border-color);
  white-space: nowrap;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border-radius: var(--radius-md);
  padding: 0.45rem 0.85rem;
  font-size: var(--text-sm);
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: 1px solid ${(p) => (p.$primary ? 'transparent' : 'var(--border-color)')};
  background: ${(p) => (p.$primary ? 'var(--accent-cyan)' : 'transparent')};
  color: ${(p) => (p.$primary ? '#04121a' : 'var(--text-secondary)')};

  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &:not(:disabled):hover {
    color: ${(p) => (p.$primary ? '#04121a' : 'var(--text-primary)')};
    border-color: ${(p) => (p.$primary ? 'transparent' : 'var(--border-strong)')};
    filter: ${(p) => (p.$primary ? 'brightness(1.08)' : 'none')};
  }
`;

const Select = styled.select`
  background: var(--surface-3);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.42rem 0.6rem;
  color: var(--text-primary);
  font-family: inherit;
  font-size: var(--text-sm);
  max-width: 15rem;

  &:focus { outline: none; border-color: var(--border-accent); }
  option { background: var(--surface-1); color: var(--text-primary); }
`;

const Spacer = styled.div`
  flex: 1;
  min-width: 0;
`;

const Panel = styled.div`
  margin-top: 0.7rem;
  padding-top: 0.7rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.35rem 0.65rem;
  border-radius: var(--radius-md);
  border: 1px solid ${(p) => (p.$on ? 'var(--border-accent)' : 'var(--border-color)')};
  background: ${(p) => (p.$on ? 'var(--accent-cyan-dim)' : 'transparent')};
  color: ${(p) => (p.$on ? 'var(--text-primary)' : 'var(--text-secondary)')};
  font-size: var(--text-sm);
  cursor: pointer;

  input { width: auto; margin: 0; accent-color: var(--accent-cyan); }
`;

const Price = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-variant-numeric: tabular-nums;
`;

const Cost = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.5;
`;

function LeadActionBar({
  selected,
  providers, provider, setProvider,
  capabilities, toggleCapability, capabilityOptions,
  cost, enriching, onEnrich,
  campaigns, campaignId, setCampaignId, launching, onLaunch,
  onSaveAsList, onSuppress, onExport, onExporting, onClear
}) {
  const [showFind, setShowFind] = useState(false);
  const n = selected.length;
  const nothingToFind = capabilities.length === 0 || !cost || cost.total === 0;

  return (
    <Dock>
      <Bar>
        <Line>
          <Count><Icon name="check" size={14} /> {n} selected</Count>

          <Button onClick={() => setShowFind((v) => !v)} aria-expanded={showFind}>
            <Icon name="search" size={14} /> Find contact details
            <Icon name={showFind ? 'chevronDown' : 'chevronRight'} size={13} />
          </Button>

          <Button onClick={onSaveAsList}>
            <Icon name="list" size={14} /> Save to pool
          </Button>

          <Button onClick={onExport} disabled={onExporting} title="Download these leads as a CSV">
            <Icon name="download" size={14} /> Export
          </Button>

          {/* Destructive, so it sits away from the buttons you actually want. */}
          <Button onClick={onSuppress} title="Never contact these people again">
            <Icon name="alert" size={14} /> Never contact
          </Button>

          {campaigns.length > 0 && (
            <>
              <Select value={campaignId} onChange={(e) => setCampaignId(e.target.value)} aria-label="Campaign to run">
                <option value="">Add to campaign…</option>
                {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
              <Button $primary disabled={launching || !campaignId} onClick={onLaunch}>
                <Icon name="play" size={14} /> {launching ? 'Starting…' : `Run on ${n}`}
              </Button>
            </>
          )}

          <Spacer />

          <Button onClick={onClear}><Icon name="close" size={14} /> Clear</Button>
        </Line>

        {showFind && (
          <Panel>
            {capabilityOptions.map((opt) => {
              const line = cost && cost.lines.find((l) => l.key === opt.key);
              return (
                <Toggle key={opt.key} $on={capabilities.includes(opt.key)}>
                  <input
                    type="checkbox"
                    checked={capabilities.includes(opt.key)}
                    onChange={() => toggleCapability(opt.key)}
                  />
                  {opt.label}
                  <Price>
                    {line
                      ? `${line.leads} lead${line.leads === 1 ? '' : 's'} · ~${line.credits} credits`
                      : `~${opt.perRow}/lead`}
                  </Price>
                </Toggle>
              );
            })}

            <Select value={provider} onChange={(e) => setProvider(e.target.value)} aria-label="Which provider to ask">
              <option value="all">Try all connected</option>
              {providers.map((p) => (
                <option key={p.name} value={p.name}>
                  {p.name}{typeof p.remaining === 'number' ? ` (${p.remaining} left)` : ''}
                </option>
              ))}
            </Select>

            <Button $primary disabled={enriching || nothingToFind} onClick={() => onEnrich(false)}>
              {enriching ? 'Looking…' : 'Find'}
            </Button>

            <Spacer />

            <Cost>
              {capabilities.length === 0
                ? 'Pick at least one thing to look for.'
                : cost && cost.total === 0
                  ? 'Everyone selected already has these — use Check again to ask anyway.'
                  : cost
                    ? `About ${cost.total} credits across ${cost.leads} lead${cost.leads === 1 ? '' : 's'}.`
                    : 'Working out the cost…'}
            </Cost>
          </Panel>
        )}
      </Bar>
    </Dock>
  );
}

export default LeadActionBar;
